import React, { Component } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@material-ui/icons';
import { createStyles, withStyles, Theme } from '@material-ui/core/styles';
import { DEFAULT_VARIABLES } from '../../utilities/src/MessageVariableProcessor';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      width: '100%',
    },
    dropdownButton: {
      minWidth: '32px',
      height: '32px',
      padding: '6px',
      borderRadius: '50%',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '14px',
      fontWeight: 600,
      color: '#333',
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
    },
    menu: {
      maxHeight: 400,
      width: '100%',
      maxWidth: 500,
      '& .MuiPaper-root': {
        maxWidth: '400px',
        maxHeight: '400px',
        overflow: 'auto',
        position: 'relative',
        zIndex: 1000,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      },
    },
    searchField: {
      padding: theme.spacing(2),
      borderBottom: '1px solid #e0e0e0',
      '& .MuiTextField-root': {
        width: '100%',
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: '20px',
        backgroundColor: '#f5f5f5',
        '& fieldset': {
          border: 'none',
        },
        '&:hover fieldset': {
          border: 'none',
        },
        '&.Mui-focused fieldset': {
          border: 'none',
        },
      },
    },
    variableItem: {
      padding: theme.spacing(1.5, 2),
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #f0f0f0',
      '&:hover': {
        backgroundColor: '#f8f9fa',
      },
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    variableName: {
      fontWeight: 600,
      fontSize: '14px',
      color: '#333',
      flex: 1,
    },
    variableCode: {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#666',
      backgroundColor: 'transparent',
      padding: '2px 4px',
      borderRadius: '4px',
    },
    variableDescription: {
      fontSize: '0.8rem',
      color: '#666',
      marginTop: theme.spacing(0.5),
    },
    noResults: {
      padding: theme.spacing(2),
      textAlign: 'center',
      color: '#666',
    },
    mobileOptimized: {
      [theme.breakpoints.down('sm')]: {
        '& .MuiMenu-paper': {
          maxWidth: '90vw',
          width: '90vw',
        },
        '& .MuiMenuItem-root': {
          padding: theme.spacing(1),
          fontSize: '0.9rem',
        },
        '& .variableCode': {
          fontSize: '0.7rem',
          padding: theme.spacing(0.2, 0.4),
        },
        '& .variableDescription': {
          fontSize: '0.75rem',
        },
      },
    },
  });

interface Props {
  classes: any;
  onVariableSelect: (variable: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface State {
  anchorEl: HTMLElement | null;
  searchQuery: string;
  filteredVariables: any[];
}

class SimpleMessageVariablesDropdown extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    
    this.state = {
      anchorEl: null,
      searchQuery: '',
      filteredVariables: DEFAULT_VARIABLES
    };
  }

  handleClick = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null, searchQuery: '', filteredVariables: DEFAULT_VARIABLES });
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    this.setState({ searchQuery: query });
    
    const filtered = DEFAULT_VARIABLES.filter(variable =>
      variable.name.toLowerCase().includes(query) ||
      variable.variable.toLowerCase().includes(query) ||
      variable.description.toLowerCase().includes(query)
    );
    
    this.setState({ filteredVariables: filtered });
  };

  handleVariableClick = (variable: string) => {
    this.props.onVariableSelect(variable);
    this.handleClose();
  };

  render() {
    const { classes, placeholder, disabled } = this.props;
    const { anchorEl, searchQuery, filteredVariables } = this.state;
    const open = Boolean(anchorEl);

    return (
      <Box className={`${classes.root} ${classes.mobileOptimized}`}>
        <Button
          className={classes.dropdownButton}
          onClick={this.handleClick}
          disabled={disabled}
        >
          {placeholder || ':a:'}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={this.handleClose}
          className={classes.menu}
          PaperProps={{
            className: classes.menu
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          getContentAnchorEl={null}
        >
          <Box className={classes.searchField}>
            <TextField
              fullWidth
              placeholder="Search"
              value={searchQuery}
              onChange={this.handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => this.handleSearchChange({ target: { value: '' } } as any)}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {filteredVariables.length === 0 ? (
            <Box className={classes.noResults}>
              <Typography>No variables found</Typography>
            </Box>
          ) : (
            filteredVariables.map((variable) => (
              <Box
                key={variable.variable}
                onClick={() => this.handleVariableClick(variable.variable)}
                className={classes.variableItem}
              >
                <Typography className={classes.variableName}>
                  {variable.name}
                </Typography>
                <Typography className={classes.variableCode}>
                  {variable.variable}
                </Typography>
              </Box>
            ))
          )}
        </Menu>
      </Box>
    );
  }
}

export default withStyles(styles)(SimpleMessageVariablesDropdown);

